/**
 * Aliquota IVA impostata dalla struttura sul prodotto.
 *
 * Il dato sul prodotto è `vat_rate` (0 | 5 | 22) più, solo ad aliquota zero,
 * `vat_nature`: il codice Natura che la fattura elettronica deve riportare per
 * dichiarare perché l'imposta non si applica. Senza quel codice lo SDI scarta
 * il documento, per questo le due voci a 0% sono distinte.
 *
 * I prodotti creati prima dell'introduzione di `vat_rate` hanno solo il vecchio
 * booleano `iva_5_percent`: il fallback li interpreta con la regola di sempre,
 * quindi continuano a funzionare senza essere migrati.
 *
 * Per aggiungere altre nature (N3 non imponibile, N2.1 fuori campo art. 7…)
 * basta una riga in VAT_OPTIONS e il valore corrispondente nell'enum lato
 * backend: nient'altro va toccato.
 */

export const VAT_OPTIONS = [
    { value: '22', label: '22%', rate: 22, nature: null },
    { value: '5', label: '5%', rate: 5, nature: null },
    { value: '0-N4', label: '0% — Esente art. 10', rate: 0, nature: 'N4' },
    { value: '0-N2.2', label: '0% — Fuori campo IVA', rate: 0, nature: 'N2.2' },
];

const DEFAULT_NATURE = 'N4';

// `??` e non `||`: con vat_rate = 0 un OR ricadrebbe erroneamente sul legacy.
export const resolveVatRate = (product) =>
    product?.vat_rate ?? (product?.iva_5_percent ? 5 : 22);

/** Opzione da mostrare nel menu per il prodotto in esame. */
export const findVatOption = (product) => {
    const rate = resolveVatRate(product);
    if (rate !== 0) {
        return VAT_OPTIONS.find(o => o.rate === rate) ?? VAT_OPTIONS[0];
    }
    const nature = product?.vat_nature ?? DEFAULT_NATURE;
    return VAT_OPTIONS.find(o => o.rate === 0 && o.nature === nature)
        ?? VAT_OPTIONS.find(o => o.rate === 0 && o.nature === DEFAULT_NATURE);
};

/**
 * Campi da scrivere in formData alla scelta dell'utente.
 *
 * Non passa da handleSelectChangeBoolean di proposito: quello fa
 * `selectedOption?.value || false`, e con un'aliquota 0 l'OR la convertirebbe
 * in false, cioè in 22%, salvando un valore sbagliato senza alcun errore.
 */
export const vatFieldsFromOption = (option) => ({
    vat_rate: option?.rate ?? 22,
    // `undefined` e non `null`: lo schema dichiara vat_nature come stringa, e un
    // null esplicito verrebbe rifiutato dalla validazione. Undefined viene
    // semplicemente omesso dal JSON, e il backend lo azzera da sé fuori dallo 0%.
    vat_nature: option?.nature ?? undefined,
});
